import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, MessageCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import api from "../lib/api";
import { waLink } from "../lib/clinic";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { AUTHOR_REF, PROVIDER_REF, SITE, breadcrumb, graph } from "../lib/schema";
import { medicalConditionNode } from "../lib/medicalConditions";
import SymptomCheckerTeaser from "../components/site/SymptomCheckerTeaser";

const ExpertiseDetail = () => {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [notFound, setNotFound] = useState(false);
  usePageMeta({
    title: item ? `${item.title} — Dr. Shantanu Gupta, Varanasi` : "Service | Dr. Shantanu Gupta",
    path: `/expertise/${slug}`,
    description: item?.tagline,
  });

  const procedureSchema = useMemo(() => {
    if (!item) return null;
    const url = `${SITE}/expertise/${slug}`;
    const nodes = [
      {
        "@type": "MedicalProcedure",
        "@id": `${url}#procedure`,
        name: item.title,
        alternateName: item.tagline,
        description: item.summary,
        url,
        procedureType: "https://schema.org/TherapeuticProcedure",
        bodyLocation: "Eye",
        performer: AUTHOR_REF,
        provider: PROVIDER_REF,
        howPerformed: (item.sections || [])
          .map((s) => s.body)
          .join("\n\n")
          .slice(0, 1500) || undefined,
        preparation: (item.hero_points || []).join(". ") || undefined,
        // Emit each section heading as a HowToStep — search engines use this
        // for expanded rich results.
        step: (item.sections || []).map((s, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: s.heading,
          text: (s.body || "").slice(0, 500),
        })),
      },
    ];
    if (item.faqs?.length) {
      nodes.push({
        "@type": "FAQPage",
        "@id": `${url}#faq`,
        mainEntity: item.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    }
    // MedicalCondition node — only emitted for slugs that map to a
    // genuine clinical condition (per lib/medicalConditions).  Cross-
    // linked to the MedicalProcedure via `possibleTreatment` so Google
    // resolves the page as "condition X + treatment Y at this clinic".
    // Unlocks Google's "About this condition" Knowledge Graph card.
    const conditionNode = medicalConditionNode(slug, url, `${url}#procedure`);
    if (conditionNode) nodes.push(conditionNode);
    nodes.push(
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Expertise", path: "/expertise" },
        { name: item.title, path: `/expertise/${slug}` },
      ])
    );
    return graph(nodes);
  }, [item, slug]);
  usePageJsonLd(procedureSchema);

  useEffect(() => {
    setNotFound(false);
    setItem(null);
    api.get(`/expertise/${slug}`).then((r) => setItem(r.data)).catch(() => setNotFound(true));
    api.get(`/expertise`).then((r) => setSiblings((r.data || []).filter((e) => e.slug !== slug))).catch(() => {});
  }, [slug]);

  if (notFound) return <div className="container-page py-32 text-center">Service not found. <Link to="/expertise" className="text-[hsl(var(--primary))]">See all services</Link></div>;
  if (!item) return <div className="container-page py-32 text-center text-muted-foreground">Loading…</div>;

  return (
    <article data-testid="expertise-detail-page">
      <section className="pt-16 pb-8 container-page">
        <Link to="/expertise" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[hsl(var(--primary))]" data-testid="back-to-expertise">
          <ArrowLeft size={14} /> All Services
        </Link>
        <div className="mt-6 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <div className="overline mb-4">Expertise</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance">{item.title}</h1>
            <p className="mt-5 prose-elegant">{item.tagline}</p>
            <p className="mt-3 prose-elegant">{item.summary}</p>
          </div>
          <aside className="lg:col-span-4">
            <div className="border border-border rounded-2xl p-6 bg-[hsl(var(--secondary))]/40 sticky top-28">
              <div className="overline mb-3">At a glance</div>
              <ul className="space-y-3">
                {(item.hero_points || []).map((p) => (
                  <li key={p} className="flex gap-2 items-start text-sm">
                    <CheckCircle2 size={16} strokeWidth={1.5} className="text-[hsl(var(--accent))] mt-0.5" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <a href={waLink(`Hello Dr. Shantanu, I'd like to know more about ${item.title}.`)} target="_blank" rel="nofollow noopener noreferrer" className="btn-primary mt-6 w-full" data-testid="detail-book-btn">
                <MessageCircle size={16} /> Book Consultation
              </a>
            </div>
          </aside>
        </div>
      </section>

      <section className="py-16 container-page grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8 space-y-12">
          {(item.sections || []).map((s) => (
            <div key={s.heading} data-testid={`section-${s.heading.replace(/\s/g, "-").toLowerCase()}`}>
              <h2 className="font-serif text-3xl leading-tight">{s.heading}</h2>
              <div className="h-[2px] w-12 bg-[hsl(var(--accent))] mt-3" />
              <p className="prose-elegant mt-5 whitespace-pre-line">{s.body}</p>
            </div>
          ))}

          {item.faqs?.length > 0 && (
            <div>
              <h2 className="font-serif text-3xl">Frequently Asked</h2>
              <div className="h-[2px] w-12 bg-[hsl(var(--accent))] mt-3 mb-6" />
              <Accordion type="single" collapsible className="border-y border-border">
                {item.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`f-${i}`} data-testid={`faq-${i}`}>
                    <AccordionTrigger className="text-left font-medium">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-[hsl(var(--foreground))]/80">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          {siblings.length > 0 && (
            <div className="not-prose mt-10" data-testid="sibling-expertise">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] mb-4">Explore more services</div>
              <h2 className="font-serif text-3xl">Other treatments at Shantanu Netralaya</h2>
              <div className="h-[2px] w-12 bg-[hsl(var(--accent))] mt-3 mb-6" />
              <div className="grid sm:grid-cols-2 gap-4">
                {siblings.slice(0, 8).map((e) => (
                  <Link
                    key={e.slug}
                    to={`/expertise/${e.slug}`}
                    data-testid={`sibling-${e.slug}`}
                    className="group border border-border rounded-xl p-5 hover:border-[hsl(var(--primary))] transition-colors"
                  >
                    <div className="font-serif text-lg leading-snug group-hover:text-[hsl(var(--primary))]">{e.title}</div>
                    <p className="mt-1.5 text-sm text-muted-foreground line-clamp-2">{e.tagline}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="lg:col-span-4">
          <div className="border border-border rounded-2xl p-6">
            <div className="overline mb-3">Reviewed by</div>
            <div className="font-serif text-xl">Dr. Shantanu Kumar Gupta</div>
            <div className="text-xs text-muted-foreground">MBBS · MS (Ophthalmology) · Retina Fellow</div>
            <div className="mt-4 text-xs text-muted-foreground">Last reviewed: 15 Feb 2026</div>
            <div className="mt-4 text-xs text-muted-foreground italic">
              This article is for general information only and does not replace professional medical advice.
            </div>
          </div>
        </aside>
      </section>

      <section className="container-page pb-16">
        <SymptomCheckerTeaser context="expertise-detail" />
      </section>
    </article>
  );
};

export default ExpertiseDetail;
